/**
 * ScrollableContentViewer Class
 *
 * This custom HTMLElement class creates a scrollable content viewer. It enables users to view content extending beyond the visible area via drag-to-scroll interactions and navigation buttons (forward and backward).
 * It is primarily designed for scenarios where content width exceeds that of the parent container, offering an intuitive and user-friendly way to access overflow content.
 *
 * Features:
 * 1. Automatic Initialization: The component initializes automatically when the content within the div exceeds the visible area.
 * 2. Drag-to-View: Users can drag to view hidden content areas.
 * 3. Forward and Backward Buttons: These buttons allow for easy navigation through the content.
 * 4. Smooth Scrolling and Transitions: Delivers a smooth scrolling experience, enhancing user interaction.
 * 5. Dynamic Content Adaptation: Flexibly adapts to content widths that change dynamically.
 *
 * Use Cases:
 * - Suitable for areas displaying wide content (such as images, tables, or text), especially when the content cannot be fully displayed on a single screen.
 * - Applicable to various scenarios—such as reports, image galleries, and article reading—to enhance content presentation and user experience.
 */
if (!customElements.get("scrollable-content-viewer")) {
  customElements.define(
    "scrollable-content-viewer",
    class ScrollableContentViewer extends HTMLElement {
      constructor() {
        super();
        this.initScrollableStatus = false; // Component installation status
        this.DRAG_THRESHOLD = 100;
        this.TRANSITION_DURING = 300;
        this.currentTranslate = 0;
        this.isClicking = false;
        this.isDragging = false;
        this.lastWindowWidth = window.innerWidth;

        this.scrollableWrapper = this.querySelector(".scrollable-content-wrapper");
        if (!this.scrollableWrapper) return;

        // Previous/Next toggle button
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.install();

        // Add a listener for screen size changes.
        if (window.Shopify.designMode || window.debug) {
          this.debounceWindowSizeChangeHandler = webvista.debounce(
            this.onWindowSizeChange.bind(this),
            500,
          );
          window.addEventListener(
            "resize",
            this.debounceWindowSizeChangeHandler,
          );
        }
      }

      disconnectedCallback() {
        // Remove the screen size change listener.
        if (this.debounceWindowSizeChangeHandler)
          window.removeEventListener(
            "resize",
            this.debounceWindowSizeChangeHandler,
          );
      }

      // Install components
      install() {
        if (this.initScrollableStatus) return;

        this.updateScrollableMetrics(); // Obtain baseline data
        this.updateInfo();
        if (this.maxTranslateValue <= 0) return; // Component functionality is not required.

        const onNavButtonClickFunction = webvista.debounce(
          this.onNavButtonClick.bind(this),
          200,
        );
        this.boundOnNavButtonClick = (event) => {
          event.preventDefault();
          const direction = event.currentTarget.name;
          onNavButtonClickFunction(direction);
        };

        if (this.prevButton) this.prevButton.addEventListener("click", this.boundOnNavButtonClick);
        if (this.nextButton) this.nextButton.addEventListener("click", this.boundOnNavButtonClick);

        // Bind Drag-and-Drop
        if (this.hasAttribute("data-draggable")) {
          this.boundDragStart = this.onDragStart.bind(this);
          this.boundDragMove = this.onDragMove.bind(this);
          this.boundDragEnd = this.onDragEnd.bind(this);
          // PC version
          this.addEventListener("mousedown", this.boundDragStart, { passive: true });
          // Mobile Version
          this.addEventListener("touchstart", this.boundDragStart, { passive: true });
        }
        this.enableTransition();
        this.toggleStatus(true);
      }

      // Reinstall
      reInstall() {
        if (this.initScrollableStatus) this.uninstall();
        this.install();
      }

      // Uninstall component
      uninstall() {
        this.clearTranslation(); // Clear the displacement transformation prior to the displacement.
        // Remove the event listener for the navigation button.
        if (this.prevButton) {
          this.prevButton.removeEventListener("click", this.boundOnNavButtonClick);
        }
        if (this.nextButton) {
          this.nextButton.removeEventListener("click", this.boundOnNavButtonClick);
        }
        // Remove drag-related event listeners to prevent duplicate definitions.
        if (this.hasAttribute("data-draggable")) {
          if (this.boundDragStart) {
            this.removeEventListener("mousedown", this.boundDragStart);
            this.removeEventListener("touchstart", this.boundDragStart);
          }
          if (this.boundDragMove) {
            this.removeEventListener("mousemove", this.boundDragMove);
            this.removeEventListener("touchmove", this.boundDragMove);
          }
          if (this.boundDragEnd) {
            this.removeEventListener("mouseup", this.boundDragEnd);
            this.removeEventListener("touchend", this.boundDragEnd);
          }
        }
        // Cancel any pending requestAnimationFrame calls.
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
        }
        this.toggleStatus(false);
      }

      /* ====== Obtain basic positioning data ====== */
      updateScrollableMetrics() {
        const wrapperRect = this.scrollableWrapper?.getBoundingClientRect();
        const firstChildRect = this.scrollableWrapper?.firstElementChild?.getBoundingClientRect(); // First child element
        const lastChildRect = this.scrollableWrapper?.lastElementChild?.getBoundingClientRect(); // The last child element
        this.scrollableClientSize = wrapperRect?.width; // Scroll container width
        if (webvista.isRTL()) {
          this.scrollableTotalSize = firstChildRect?.right - lastChildRect?.left; // Total content width
        } else {
          this.scrollableTotalSize = lastChildRect?.right - firstChildRect?.left; // Total content width
        }
        this.maxTranslateValue = this.scrollableTotalSize - this.scrollableClientSize; // Scrollable distance
      }

      /* ====== Toggle Carousel Installation Status ====== */
      toggleStatus(status = false) {
        this.initScrollableStatus = status;
        this.classList.toggle("scrollable--installed", status);
      }

      /* ====== Navigation button click handler ====== */
      onNavButtonClick(direction = "next") {
        this.slideContentByScreen(direction);
      }

      /* ====== Handling Screen Size Changes ====== */
      onWindowSizeChange() {
        const currentWidth = window.innerWidth;
        if (currentWidth !== this.lastWindowWidth) {
          this.reInstall();
          this.lastWindowWidth = currentWidth;
        }
      }

      /* ====== Swipe across a screen ====== */
      slideContentByScreen(direction = "next") {
        if (!this.initScrollableStatus) return;

        if (direction === "next") {
          this.currentTranslate = webvista.ceilToMultiple(
            this.currentTranslate + 5,
            this.scrollableClientSize,
          );
        } else {
          this.currentTranslate = webvista.floorToMultiple(
            this.currentTranslate - 5,
            this.scrollableClientSize,
          );
        }
        this.enableTransition();
        this.applyTranslation();
        this.updateInfo();
      }

      /* ====== Scroll to the position of the specified child element. ====== */
      slideContentByItem(itemElement) {
        if (
          !itemElement ||
          !this.initScrollableStatus ||
          itemElement.parentElement !== this.scrollableWrapper
        )
          return;

        const itemRect = itemElement.getBoundingClientRect();
        const firstElementRect = this.scrollableWrapper.firstElementChild.getBoundingClientRect();

        if (webvista.isRTL()) {
          this.currentTranslate =
            firstElementRect.right -
            itemRect.right -
            firstElementRect.width / 2;
        } else {
          this.currentTranslate =
            itemRect.left - firstElementRect.left - firstElementRect.width / 2;
        }
        this.enableTransition();
        this.applyTranslation();
        this.updateInfo();
      }

      /* ====== Apply displacement transformation :: Can be used independently, but does not change the index value ====== */
      applyTranslation() {
        this.correctTranslate(); // Correct the value of currentTranslate.
        this.scrollableWrapper.style.transform = webvista.isRTL()
          ? `translateX(${this.currentTranslate}px)`
          : `translateX(${-this.currentTranslate}px)`;
      }

      /* ====== Corrects the translation value
       * This method ensures that the current translation value (this.currentTranslate) does not exceed the defined boundaries.
       * During dragging, a threshold is applied to allow for a certain degree of overshooting, creating an elastic effect.
       * If the translation exceeds the allowed boundaries, it is reset to the boundary value. ====== */
      correctTranslate() {
        // Drag activation threshold with elastic effect.
        let threshold = 0;
        if (this.isClicking) threshold = this.DRAG_THRESHOLD;
        const boundary = [-threshold, this.maxTranslateValue + threshold];

        if (this.currentTranslate > boundary[1]) {
          this.currentTranslate = boundary[1];
          this.setAttribute("data-move-exceed", "next");
        } else if (this.currentTranslate < boundary[0]) {
          this.currentTranslate = boundary[0];
          this.setAttribute("data-move-exceed", "pre");
        } else {
          this.removeAttribute("data-move-exceed");
        }
      }

      /* ====== Handle the drag-start event. ====== */
      onDragStart(event) {
        document.addEventListener("mousemove", this.boundDragMove);
        document.addEventListener("mouseup", this.boundDragEnd);
        document.addEventListener("touchmove", this.boundDragMove);
        document.addEventListener("touchend", this.boundDragEnd);

        this.isClicking = true;
        this.isDragging = false;

        if (event["touches"]) {
          this.dragStartPos = event["touches"][0].clientX;
        } else {
          this.dragStartPos = event.clientX;
        }
        this.currentPos = this.dragStartPos;
        this.preTranslate = this.currentTranslate;
        this.disableTransition(); // Disable transition effects
      }

      /* ====== Handle drag-and-move events. ====== */
      onDragMove(event) {
        if (!this.isClicking) return;

        if (event["touches"]) {
          this.currentPos = event["touches"][0].clientX;
        } else {
          this.currentPos = event.clientX;
        }

        const dragOffset = this.dragStartPos - this.currentPos;

        if (Math.abs(dragOffset) > 1) {
          event.preventDefault();
          this.isDragging = true;
          this.classList.add("scrollable--is-dragging");

          // Cancel the previous animation frame request.
          if (this.rafId) {
            cancelAnimationFrame(this.rafId);
          }

          this.rafId = requestAnimationFrame(() => {
            if (webvista.isRTL()) {
              this.currentTranslate = this.preTranslate - dragOffset;
            } else {
              this.currentTranslate = this.preTranslate + dragOffset;
            }
            this.applyTranslation(); // Update the element's position.
          });
        }
      }

      /* ====== Handle the drag-end event. ====== */
      onDragEnd(event) {
        document.removeEventListener("mousemove", this.boundDragMove);
        document.removeEventListener("mouseup", this.boundDragEnd);
        document.removeEventListener("touchmove", this.boundDragMove);
        document.removeEventListener("touchend", this.boundDragEnd);

        this.isClicking = false;
        // No movement
        if (!this.isDragging) return;

        event.stopPropagation();

        this.classList.remove("scrollable--is-dragging");

        // Cancel the currently executing animation.
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
        }

        let direction;
        if (webvista.isRTL()) {
          direction = this.dragStartPos - this.currentPos > 0 ? "previous" : "next";
        } else {
          direction = this.dragStartPos - this.currentPos > 0 ? "next" : "previous";
        }

        this.slideContentByScreen(direction);
      }

      /* ====== Update component information ====== */
      updateInfo() {
        if (this.prevButton)
          this.prevButton.toggleAttribute(
            "disabled",
            this.currentTranslate <= 0,
          );
        if (this.nextButton)
          this.nextButton.toggleAttribute(
            "disabled",
            this.currentTranslate >= this.maxTranslateValue,
          );
      }

      // Disable transition effects
      disableTransition() {
        this.scrollableWrapper.style.transition = "";
      }

      // Enable transition effects
      enableTransition() {
        this.scrollableWrapper.style.transition = `transform ${this.TRANSITION_DURING}ms`;
      }

      /* ====== Clear displacement transformation ====== */
      clearTranslation() {
        this.currentTranslate = 0;
        this.scrollableWrapper.style = "";
      }
    },
  );
}
